import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { BaseWidgetProps } from '../../types/dashboard';
import { GET_MY_MESSAGES } from '../../graphql/messages';
import { MessageSquare, User, Clock, ArrowRight } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

interface Message {
  id: string;
  content: string;
  createdAt: string;
  isRead: boolean;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    role: string;
  };
  conversation: {
    id: string;
    participants: Array<{
      id: string;
      firstName: string;
      lastName: string;
      role?: string;
    }>;
  };
}

interface Conversation {
  id: string;
  lastMessage: Message;
  unreadCount: number;
  participants: Array<{
    id: string;
    firstName: string;
    lastName: string;
    role?: string;
  }>;
}

// Type definitions for Apollo queries
interface MyMessagesData {
  myMessages: {
    items: Message[];
    nextToken?: string;
  };
}

export function RecentMessagesWidget({
  widget,
  isEditing: _isEditing,
  onConfigChange: _onConfigChange,
  onRemove: _onRemove,
  className = '',
}: BaseWidgetProps) {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  
  const { data, loading, error, refetch } = useQuery<MyMessagesData>(GET_MY_MESSAGES, {
    variables: {
      limit: widget.config.conversationLimit || 5,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: widget.config.autoRefresh ? 15000 : 0, // Refresh every 15 seconds if enabled
  });

  useEffect(() => {
    if (data?.myMessages?.items) {
      // Group messages by conversation and get the latest message for each
      const conversationMap = new Map<string, Conversation>();
      
      data.myMessages.items.forEach((message: Message) => {
        const conversationId = message.conversation.id;
        const existing = conversationMap.get(conversationId);
        
        if (!existing || new Date(message.createdAt) > new Date(existing.lastMessage.createdAt)) {
          conversationMap.set(conversationId, {
            id: conversationId,
            lastMessage: message,
            unreadCount: existing?.unreadCount || (message.isRead ? 0 : 1),
            participants: message.conversation.participants,
          });
        } else if (!message.isRead) {
          existing.unreadCount += 1;
        }
      });
      
      let conversationList = Array.from(conversationMap.values());
      
      // Filter to show only unread if configured
      if (widget.config.showUnreadOnly) {
        conversationList = conversationList.filter(conv => conv.unreadCount > 0);
      }
      
      // Sort by most recent message
      conversationList.sort((a, b) => 
        new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime()
      );
      
      setConversations(conversationList);
    }
  }, [data, widget.config.showUnreadOnly]);

  const getParticipantName = (conversation: Conversation, currentUserId?: string) => {
    const otherParticipant = conversation.participants.find(p => p.id !== currentUserId);
    return otherParticipant 
      ? `${otherParticipant.firstName} ${otherParticipant.lastName}`
      : 'Unknown';
  };

  const truncateMessage = (content: string, maxLength = 60) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const totalUnreadCount = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  if (loading) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-aalaw-red" />
            Recent Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={`h-full ${className}`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-aalaw-red" />
            Recent Messages
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-gray-600">Failed to load messages</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4" size="sm">
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`h-full ${className}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-aalaw-red" />
            Recent Messages
            {totalUnreadCount > 0 && (
              <Badge variant="destructive" className="ml-2">
                {totalUnreadCount}
              </Badge>
            )}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/messages')}
            className="flex items-center gap-1 text-aalaw-red hover:text-aalaw-red"
          >
            View All
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No messages</h3>
              <p className="text-gray-600">
                {widget.config.showUnreadOnly 
                  ? 'No unread messages found.'
                  : 'Recent messages will appear here.'
                }
              </p>
            </div>
          ) : (
            conversations.map((conversation) => (
              <div
                key={conversation.id}
                className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/messages?conversation=${conversation.id}`)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-aalaw-red rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-aalaw-charcoal text-sm truncate">
                        {getParticipantName(conversation)}
                      </h4>
                      <p className="text-xs text-gray-600">
                        {conversation.lastMessage.sender.firstName} {conversation.lastMessage.sender.lastName}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {conversation.unreadCount > 0 && (
                      <Badge variant="destructive" className="text-xs">
                        {conversation.unreadCount}
                      </Badge>
                    )}
                    <div className="text-xs text-gray-500">
                      {formatDistanceToNow(new Date(conversation.lastMessage.createdAt), { addSuffix: true })}
                    </div>
                  </div>
                </div>
                
                <div className="text-sm text-gray-700 line-clamp-2">
                  {truncateMessage(conversation.lastMessage.content)}
                </div>
                
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock className="w-3 h-3" />
                    {format(new Date(conversation.lastMessage.createdAt), 'MMM d, h:mm a')}
                  </div>
                  
                  {!conversation.lastMessage.isRead && (
                    <div className="w-2 h-2 bg-aalaw-red rounded-full"></div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              {widget.config.showUnreadOnly ? 'Unread only' : 'All conversations'}
            </span>
            <div className="flex items-center gap-2">
              {widget.config.autoRefresh && (
                <span className="text-xs text-green-600">Auto-refresh</span>
              )}
              <Button onClick={() => refetch()} variant="ghost" size="sm">
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}